import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Send, LifeBuoy, CheckCircle, User, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import SEOHead from '../components/SEOHead';
import ReCaptcha, { useCaptcha } from '../components/ReCaptcha';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SupportPage = () => {
  const location = useLocation();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [ticketId, setTicketId] = useState(null);
  const [isNew, setIsNew] = useState(true);
  const [trackId, setTrackId] = useState('');
  const [conversation, setConversation] = useState(null);
  const [trackError, setTrackError] = useState('');
  const [tracking, setTracking] = useState(false);
  const [replyMsg, setReplyMsg] = useState('');
  const [replying, setReplying] = useState(false);
  
  // CAPTCHA
  const { captchaToken, captchaKey, resetCaptcha, onVerify, onExpire, onError, isVerified, captchaConfig } = useCaptcha();
  const captchaRequired = captchaConfig?.enabled && captchaConfig?.protect_support;

  // Reset form when URL changes (e.g., clicking Support link from footer)
  useEffect(() => {
    setForm({ name: '', email: '', subject: '', message: '' });
    setTicketId(null);
    setConversation(null);
    setTrackId('');
    resetCaptcha();
    setTrackError('');
  }, [location.search]);

  const submitTicket = async (e) => {
    e.preventDefault();
    if (!form.email || !form.message) return;
    if (captchaRequired && !isVerified) {
      alert('Veuillez valider le CAPTCHA');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/support/tickets`, { ...form, captcha_token: captchaToken });
      setTicketId(res.data.ticket_id);
      setIsNew(res.data.is_new);
      // Load the conversation right away
      loadConversation(res.data.ticket_id);
      resetCaptcha();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  const loadConversation = async (id) => {
    setTracking(true);
    setTrackError('');
    try {
      const res = await axios.get(`${API}/support/tickets/${id}`);
      setConversation(res.data);
      setTicketId(id);
    } catch {
      setTrackError('Conversation non trouvée.');
      setConversation(null);
    } finally {
      setTracking(false);
    }
  };

  const trackTicket = async (e) => {
    e.preventDefault();
    if (!trackId.trim()) return;
    loadConversation(trackId.trim());
  };

  const sendReply = async () => {
    if (!replyMsg.trim() || !ticketId) return;
    setReplying(true);
    try {
      await axios.post(`${API}/support/tickets/${ticketId}/reply`, { message: replyMsg.trim() });
      setReplyMsg('');
      loadConversation(ticketId);
    } catch (err) {
      alert('Erreur lors de l\'envoi');
    } finally {
      setReplying(false);
    }
  };

  const statusLabel = (s) => {
    if (s === 'open') return { text: 'En attente', cls: 'bg-yellow-500/15 text-yellow-400' };
    if (s === 'answered') return { text: 'Répondu', cls: 'bg-green-500/15 text-green-400' };
    return { text: 'Fermé', cls: 'bg-blue-500/15 text-blue-300' };
  };

  const showConversationView = conversation || ticketId;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 text-white">
      <SEOHead 
        title="Support" 
        description="Contactez le support SENDBOX pour toute question ou problème. Notre équipe est disponible pour vous aider avec votre compte et vos transferts." 
      />
      {/* Header */}
      <NavigationMenu />

      <div className="max-w-3xl mx-auto px-6 pt-28 pb-24">
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 bg-blue-500/20 rounded-xl">
            <LifeBuoy className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Support technique</h1>
            <p className="text-blue-300/60 text-sm">Envoyez-nous un message, nous vous répondrons rapidement</p>
          </div>
        </div>

        {/* Conversation view */}
        {showConversationView && conversation ? (
          <div data-testid="conversation-view">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-white">{conversation.subject}</h2>
                <p className="text-blue-300/50 text-xs">Ref: #{conversation.ticket_id} - {conversation.email}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusLabel(conversation.status).cls}`}>
                  {statusLabel(conversation.status).text}
                </span>
                <Button onClick={() => { setConversation(null); setTicketId(null); }} variant="outline" size="sm" className="border-blue-500/20 text-blue-300 text-xs" data-testid="back-to-form-btn">
                  Nouveau message
                </Button>
              </div>
            </div>

            {/* Messages thread */}
            <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl overflow-hidden" data-testid="messages-thread">
              <div className="max-h-[450px] overflow-y-auto p-4 space-y-3" data-testid="messages-list">
                {(conversation.messages || []).map((m, i) => (
                  <div key={i} className={`flex ${m.sender === 'admin' ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-[80%] rounded-2xl p-3 ${
                      m.sender === 'admin'
                        ? 'bg-emerald-500/10 border border-emerald-500/20 rounded-tl-md'
                        : 'bg-blue-500/10 border border-blue-500/20 rounded-tr-md'
                    }`} data-testid={`message-${i}`}>
                      <div className="flex items-center gap-1.5 mb-1">
                        {m.sender === 'admin' ? (
                          <Shield className="w-3 h-3 text-emerald-400" />
                        ) : (
                          <User className="w-3 h-3 text-blue-400" />
                        )}
                        <span className={`text-[10px] font-medium ${m.sender === 'admin' ? 'text-emerald-400' : 'text-blue-400'}`}>
                          {m.sender === 'admin' ? 'Support' : 'Vous'}
                        </span>
                        <span className="text-[10px] text-blue-300/30">
                          {m.created_at ? new Date(m.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                      <p className="text-white text-sm leading-relaxed">{m.message}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply input */}
              {conversation.status !== 'closed' && (
                <div className="border-t border-blue-500/20 p-3 flex gap-2" data-testid="reply-section">
                  <input
                    type="text"
                    value={replyMsg}
                    onChange={e => setReplyMsg(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && sendReply()}
                    placeholder="Votre message..."
                    className="flex-1 bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                    data-testid="reply-input"
                  />
                  <Button onClick={sendReply} disabled={replying || !replyMsg.trim()} className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white" data-testid="reply-send-btn">
                    {replying ? '...' : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              )}
              {conversation.status === 'closed' && (
                <div className="border-t border-blue-500/20 p-3 text-center text-blue-300/40 text-sm">
                  Cette conversation est fermée
                </div>
              )}
            </div>
          </div>
        ) : ticketId && !conversation ? (
          <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-8 text-center" data-testid="ticket-success">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">{isNew ? 'Conversation créée !' : 'Message ajouté !'}</h2>
            <p className="text-blue-300/70 mb-4">Votre numéro de suivi :</p>
            <p className="text-2xl font-mono text-blue-400 bg-slate-800/50 inline-block px-6 py-3 rounded-xl" data-testid="ticket-id-display">{ticketId}</p>
            <p className="text-blue-300/50 text-sm mt-4">Chargement de la conversation...</p>
          </div>
        ) : (
          /* New message form */
          <form onSubmit={submitTicket} className="bg-slate-800/50 border border-blue-500/20 rounded-2xl p-6 space-y-4" data-testid="support-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-blue-300 mb-1.5">Nom (optionnel)</label>
                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                  placeholder="Votre nom" data-testid="support-name-input" />
              </div>
              <div>
                <label className="block text-sm text-blue-300 mb-1.5">Email *</label>
                <input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                  placeholder="votre@email.com" data-testid="support-email-input" />
              </div>
            </div>
            <div>
              <label className="block text-sm text-blue-300 mb-1.5">Sujet *</label>
              <input type="text" required value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}
                className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                placeholder="Ex: Problème de téléchargement" data-testid="support-subject-input" />
            </div>
            <div>
              <label className="block text-sm text-blue-300 mb-1.5">Message *</label>
              <textarea required rows={5} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
                className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 resize-none"
                placeholder="Décrivez votre problème en détail..." data-testid="support-message-input" />
            </div>
            
            {/* CAPTCHA */}
            {captchaRequired && (
            <div className="flex justify-center py-2">
              <ReCaptcha 
                key={captchaKey}
                onVerify={onVerify} 
                onExpire={onExpire}
                onError={onError}
                siteKey={captchaConfig?.site_key}
              />
            </div>
            )}
            
            <div className="flex justify-end">
              <Button type="submit" disabled={submitting || (captchaRequired && !isVerified)} className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white disabled:opacity-50" data-testid="support-submit-btn">
                {submitting ? 'Envoi...' : <><Send className="w-4 h-4 mr-2" /> Envoyer</>}
              </Button>
            </div>
          </form>
        )}

        {/* Ticket Tracking */}
        {!showConversationView && (
          <div className="mt-10 bg-slate-800/30 border border-blue-500/10 rounded-2xl p-6">
            <h3 className="text-base font-semibold text-white mb-4">Reprendre une conversation</h3>
            <form onSubmit={trackTicket} className="flex gap-3" data-testid="track-form">
              <input type="text" value={trackId} onChange={e => setTrackId(e.target.value)}
                placeholder="Numéro de conversation (ex: a1b2c3d4)"
                className="flex-1 bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                data-testid="track-id-input" />
              <Button type="submit" disabled={tracking} variant="outline" className="border-blue-500/30 text-blue-300" data-testid="track-submit-btn">
                {tracking ? '...' : 'Ouvrir'}
              </Button>
            </form>
            {trackError && <p className="text-red-400 text-sm mt-3" data-testid="track-error">{trackError}</p>}
          </div>
        )}
      </div>

      {/* Footer */}
      <DynamicFooter fixed={true} />
    </div>
  );
};

export default SupportPage;
